import type { OrderItemWithStock, ProductVariantPickerResource, SharedOrder } from '@storefront/resources';

export function addToCartEvent(item: ProductVariantPickerResource | OrderItemWithStock, requestedAmount: number, currency: string) {
  if (getItemType(item) === 'FLEXIBLE') requestedAmount = 1;

  return {
    'event': 'add_to_cart',
    'ecommerce': {
      'currency': currency,
      'value': calculateItemTotalPrice(item, requestedAmount),
      'items': [{
        'item_name': getItemName(item),
        'item_id': item.productId,
        'price': Number(Number(item.priceGross).toFixed(2)),
        'quantity': requestedAmount,
        ...(getItemType(item) !== 'FLEXIBLE' && {
          quantity: requestedAmount,
          discount: calculateItemDiscount(item),
        }),
      }],
    },
  };
}

export function addWishlistItemsToCartEvent(items: ProductVariantPickerResource[] | OrderItemWithStock[], currency: string) {
  const itemsToAdd: { item_name: string | null, item_id: number, price: number, quantity: string, discount: number }[] = [];


  items.forEach((item) => {
    if (getItemType(item) === 'FLEXIBLE') return;

    itemsToAdd.push({
      'item_name': getItemName(item),
      'item_id': item.productId,
      'price': Number(Number(item.priceGross).toFixed(2)),
      'quantity': item.stockUnitBase,
      'discount': calculateItemDiscount(item),
    });
  });

  return {
    'event': 'add_to_cart',
    'ecommerce': {
      'currency': currency,
      'value': itemsToAdd.reduce((total, item) => total + item.price * Number(item.quantity), 0),
      'items': itemsToAdd,
    },
  };
}


export function removeFromCartEvent(item: ProductVariantPickerResource | OrderItemWithStock, requestedAmount: number, currency: string) {
  //In context of onAmountChange in CartItem, there may be a case when user decreases amount - for example, from 5 to 3; then requestedAmount will be -2, so we have to make it positive
  requestedAmount = Math.abs(requestedAmount);
  if (getItemType(item) === 'FLEXIBLE') requestedAmount = 1;

  return {
    'event': 'remove_from_cart',
    'ecommerce': {
      'currency': currency,
      'value': calculateItemTotalPrice(item, requestedAmount),
      'items': [{
        'item_name': getItemName(item),
        'item_id': item.productId,
        'price': Number(Number(item.priceGross).toFixed(2)),
        'quantity': requestedAmount,
        ...(getItemType(item) !== 'FLEXIBLE' && {
          quantity: requestedAmount,
          discount: calculateItemDiscount(item),
        }),
      }],
    },
  };
}


export function clearCartEvent(cart: SharedOrder, currency: string) {
  const mapped = cart.items.map((item) => {
    return {
      'item_name': item.variantTitle,
      'item_id': item.productId,
      'price': Number(Number(item.priceGross).toFixed(2)),
      'quantity': 1,
      ...(getItemType(item) !== 'FLEXIBLE' && {
        quantity: Number(item.amount),
        discount: calculateItemDiscount(item),
      }),
    };
  });

  return {
    'event': 'remove_from_cart',
    'ecommerce': {
      'currency': currency,
      'value': cart.itemsGross,
      'items': mapped,
    },
  };
}

export function viewCartEvent(cart: SharedOrder | null, currency: string) {
  let mapped: any = [];

  if (cart) {
    mapped = cart.items.map((item) => {
      return {
        'item_name': item.variantTitle,
        'item_id': item.productId,
        'price': Number(Number(item.priceGross).toFixed(2)),
        'quantity': 1,
        ...(getItemType(item) !== 'FLEXIBLE' && {
          quantity: Number(item.amount),
          discount: calculateItemDiscount(item),
        }),
      };
    });
  }

  return {
    'event': 'view_cart',
    'ecommerce': {
      'currency': currency,
      'value': Number(Number(cart?.itemsGross ?? 0).toFixed(2)),
      'items': mapped ?? [],
    },
  };
}

function calculateItemTotalPrice(item: ProductVariantPickerResource | OrderItemWithStock, requestedAmount: number): number {
  return Number((Number(item.priceGross) * requestedAmount).toFixed(2));
}

function calculateItemDiscount(item: ProductVariantPickerResource | OrderItemWithStock): number {
  if (item.regularGross === null) {
    return 0;
  }

  const regularGross = Number(item.regularGross);
  const priceGross = Number(item.priceGross);

  return Number((regularGross - priceGross).toFixed(2));
}

function getItemName(
  item: ProductVariantPickerResource | OrderItemWithStock,
): string | null {
  if ('title' in item) {
    // ProductVariantPickerResource
    return item.title;
  }

  // OrderItemWithStock
  return item.variantTitle;
}

function getItemType(
  item: ProductVariantPickerResource | OrderItemWithStock,
): string | null {
  if ('type' in item) {
    // ProductVariantPickerResource
    return item.type;
  }

  // OrderItemWithStock
  return item.productType;
}